import type { PostgrestError } from '@supabase/supabase-js';

/** Normalized application error surfaced by the repository layer. */
export class AppError extends Error {
  readonly code: string | undefined;
  override readonly cause: unknown;

  constructor(message: string, code?: string, cause?: unknown) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.cause = cause;
  }
}

type PostgrestResult<T> = { data: T | null; error: PostgrestError | null };

/** Unwrap a Supabase result, throwing `AppError` on failure or missing data. */
export function unwrap<T>(res: PostgrestResult<T>): T {
  if (res.error) throw new AppError(res.error.message, res.error.code, res.error);
  if (res.data === null) throw new AppError('Expected data but received none');
  return res.data;
}

/** Unwrap a Supabase result that may legitimately be null (e.g. `.maybeSingle()`). */
export function unwrapMaybe<T>(res: PostgrestResult<T>): T | null {
  if (res.error) throw new AppError(res.error.message, res.error.code, res.error);
  return res.data;
}

/** Human-readable message from any thrown value. */
export function toMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (error instanceof AppError || error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return fallback;
}
